import React, { ReactElement } from 'react';
import { Tag as AntTag } from 'antd';
import styled, { css } from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';

enum TagColor {
  DEFAULT = 'default',
  GREEN = 'green',
  ORANGE = 'orange',
  YELLOW = 'gold',
  RED = 'red',
  GRAY = 'gray',
  PINK = 'pink',
}

interface TagProps {
  children: React.ReactNode;
  bordered?: boolean;
  color?: TagColor;
  onClose?: () => void;
  onClick?: () => void;
  className?: string;
  customStyle?: CustomStyle;
  suffix?: React.ReactNode;
  prefix?: React.ReactNode;
}

interface CustomStyle {
  marginBottom?: CSSSize | 0;
  color?: string;
  backgroundColor?: string;
}

function Tag({
  children,
  onClose,
  onClick,
  bordered = false,
  color = TagColor.DEFAULT,
  className,
  customStyle,
  suffix,
  prefix,
}: TagProps): ReactElement {
  const antOnClose = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    onClose && onClose();
  };

  const isCustomColor = color === TagColor.RED || color === TagColor.GRAY;

  return (
    <StyledTag
      onClose={antOnClose}
      bordered={bordered}
      closable={onClose !== undefined}
      onClick={onClick}
      color={isCustomColor ? undefined : color}
      className={className}
      customstyle={customStyle}
      $variant={color}
    >
      <Wrapper>
        {suffix}
        {children}
        {prefix}
      </Wrapper>
    </StyledTag>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTag = styled(AntTag)<{ customstyle?: CustomStyle; $variant?: TagColor }>`
  &.ant-tag {
    margin-bottom: ${props => props.customstyle?.marginBottom};
  }
  ${props =>
    props.$variant === TagColor.RED &&
    css`
      &.ant-tag {
        background-color: ${colorsPalette.red50};
        border-color: ${colorsPalette.red200};
        color: ${colorsPalette.red500};
      }
    `}
  ${props =>
    props.$variant === TagColor.GRAY &&
    css`
      &.ant-tag {
        background-color: ${colorsPalette.lightGray100};
        border-color: ${colorsPalette.lightGray100};
        color: ${colorsPalette.gray600};
      }
    `}
  ${props =>
    props.$variant === TagColor.PINK &&
    css`
      &.ant-tag {
        background-color: ${colorsPalette.pink.background};
        border-color: ${colorsPalette.pink.border};
        color: ${colorsPalette.pink.text};
      }
    `}
`;

export { Tag, TagColor };
